//#region IMPORTS
import { GlobeAltIcon } from "@heroicons/react/24/outline";
import { useAppDispatch, useAppSelector } from "../../store/configureStore";
import { setInstoreOrderParams } from "../../features/instoreOrders/instoreOrdersSlice";
import { SortingStateOption } from "../../models/sorting";
import { useTranslation } from "react-i18next";
import MainTableHeaderCell from "../_layout/MainTableHeaderCell";
//#endregion

//#region INTERFACE
interface Props {
  onToggleAll?: () => void;
  enableSelection?: boolean;
  someSelected?: boolean;
  allSelected?: boolean;
  isAdmin?: boolean;
  isPicker?: boolean;
  isHQ?: boolean;
  isShop?: boolean;
  showActions?: boolean;
  showDecline?: boolean;
}
//#endregion

export default function InstoreOrdersTableHeader({
  onToggleAll,
  enableSelection = false,
  someSelected = false,
  allSelected = false,
  isAdmin = false,
  isPicker = false,
  isHQ = false,
  isShop = false,
  showActions = false,
  showDecline = false,
}: Props) {
  //#region SETUP
  // Translation
  const { t } = useTranslation();

  // Redux
  const dispatch = useAppDispatch();
  const { instoreOrder_params, loading } = useAppSelector(
    (state) => state.instoreOrders
  );
  //#endregion

  //#region LOGIC

  // Set order sorting
  const sortChangedHandler = (option: SortingStateOption) => {
    if (option.column !== null) {
      dispatch(
        setInstoreOrderParams({
          sort: JSON.stringify(option),
        })
      );
    } else {
      dispatch(setInstoreOrderParams({ sort: undefined }));
    }
  };
  //#endregion

  //#region RENDER
  return (
    <thead>
      <tr>
        {/* Checkbox */}
        {enableSelection && (
          <th
            className="whitespace-nowrap pr-2 py-3.5 text-left text-xs font-semibold text-gray-900 w-8"
            scope="col"
          >
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-600"
              checked={allSelected}
              ref={(el) => {
                if (el) el.indeterminate = someSelected;
              }}
              onChange={onToggleAll}
            />
          </th>
        )}

        {/* read */}
        <th
          className="whitespace-nowrap pr-4 py-3.5 text-left text-xs font-semibold text-gray-900"
          scope="col"
        />

        {/* external_store_id */}
        {!isShop && (
          <MainTableHeaderCell
            sortChangedHandler={(option: SortingStateOption) =>
              sortChangedHandler(option)
            }
            headerText={t("rituals.storeId")}
            className="whitespace-nowrap pr-4 py-3.5 text-left text-xs text-gray-900"
            disabled={loading}
            sortName="external_store_id"
            params={instoreOrder_params}
          />
        )}

        {/* order_date */}
        <MainTableHeaderCell
          sortChangedHandler={(option: SortingStateOption) =>
            sortChangedHandler(option)
          }
          headerText={t("rituals.orderDate")}
          className="whitespace-nowrap pr-4 py-3.5 text-left text-xs text-gray-900"
          disabled={loading}
          sortName="order_date"
          params={instoreOrder_params}
        />

        {/* order_code */}
        <MainTableHeaderCell
          sortChangedHandler={(option: SortingStateOption) =>
            sortChangedHandler(option)
          }
          headerText={t("rituals.orderCode")}
          className="whitespace-nowrap pr-4 py-3.5 text-left text-xs text-gray-900"
          sortName="order_code"
          disabled={loading}
          params={instoreOrder_params}
        />

        {/* order_type */}
        <MainTableHeaderCell
          sortChangedHandler={(option: SortingStateOption) =>
            sortChangedHandler(option)
          }
          headerText={t("rituals.orderType")}
          className="whitespace-nowrap pr-4 py-3.5 text-left text-xs text-gray-900"
          disabled={loading}
          sortName="order_type_instore_name"
          params={instoreOrder_params}
        />

        {/* order_status */}
        <MainTableHeaderCell
          sortChangedHandler={(option: SortingStateOption) =>
            sortChangedHandler(option)
          }
          headerText={t("rituals.orderStatus")}
          className="whitespace-nowrap pr-4 py-3.5 text-left text-xs text-gray-900"
          disabled={loading}
          sortName="order_status"
          params={instoreOrder_params}
        />

        {/* export_reference (set on export; not sortable) — picker/admin only */}
        {(isAdmin || isPicker) && (
          <MainTableHeaderCell
            headerText={t("rituals.exportReference", "export ref.")}
            className="whitespace-nowrap pr-4 py-3.5 text-left text-xs text-gray-900"
            disabled={loading}
          />
        )}

        {/* store_language */}
        <MainTableHeaderCell
          sortChangedHandler={(option: SortingStateOption) =>
            sortChangedHandler(option)
          }
          headerText={t("rituals.language")}
          icon={<GlobeAltIcon className="w-4 h-4" />}
          className="whitespace-nowrap pr-4 py-3.5 text-left text-xs text-gray-900"
          disabled={loading}
          sortName="store_language"
          params={instoreOrder_params}
        />

        {/* delivery_date */}
        {!isShop && (
          <MainTableHeaderCell
            sortChangedHandler={(option: SortingStateOption) =>
              sortChangedHandler(option)
            }
            headerText={t("rituals.deliveryDate")}
            className="whitespace-nowrap pr-4 py-3.5 text-left text-xs text-gray-900"
            disabled={loading}
            sortName="delivery_date"
            params={instoreOrder_params}
          />
        )}

        {/* store_opening_date */}
        {!isShop && (
          <MainTableHeaderCell
            sortChangedHandler={(option: SortingStateOption) =>
              sortChangedHandler(option)
            }
            headerText={t("rituals.openingDate")}
            className="whitespace-nowrap pr-4 py-3.5 text-left text-xs text-gray-900"
            disabled={loading}
            sortName="store_opening_date"
            params={instoreOrder_params}
          />
        )}

        {/* article_amount — min-width matches the row cell, which holds the
            count plus the items-overview button. */}
        <MainTableHeaderCell
          headerText={t("rituals.items")}
          className="whitespace-nowrap pr-4 py-3.5 text-left text-xs text-gray-900 min-w-[8rem]"
          disabled={loading}
          params={instoreOrder_params}
        />

        {/* perso indicator (picker/admin) — "perso" is jargon, not translated */}
        {(isPicker || isAdmin) && (
          <MainTableHeaderCell
            headerText="perso"
            className="whitespace-nowrap pr-4 py-3.5 text-left text-xs text-gray-900"
          />
        )}

        {/* comment a.k.a. extraProjects ;) */}
        <MainTableHeaderCell
          headerText={t("rituals.extraProjects")}
          className="whitespace-nowrap pr-4 py-3.5 text-left text-xs text-gray-900"
        />

        {/* track_and_trace */}
        <MainTableHeaderCell
          headerText={t("rituals.parcelInfo")}
          className="whitespace-nowrap pr-4 py-3.5 text-left text-xs text-gray-900"
        />

        {/* Decline toggle column (admin, Approve preset only) */}
        {showDecline && (
          <MainTableHeaderCell
            headerText=""
            className="whitespace-nowrap pr-4 py-3.5 text-left text-xs text-gray-900"
          />
        )}

        {/* Actions column */}
        {showActions && (
          <MainTableHeaderCell
            headerText="actions"
            className="whitespace-nowrap pl-2 pr-4 py-3.5 text-left text-xs text-gray-900"
          />
        )}

        {/* delete (admin only) */}
        {isAdmin && (
          <MainTableHeaderCell
            headerText=""
            className="whitespace-nowrap pr-4 py-3.5 text-left text-xs text-gray-900"
          />
        )}
      </tr>
    </thead>
  );
  //#endregion
}
